import React from 'react';
import styled from 'styled-components';
import { Position } from '../../services/positionService';

interface PositionCardProps {
  position: Position;
  onClick?: () => void;
}

const Card = styled.div`
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  transition: transform 0.2s;

  &:hover {
    transform: ${({ onClick }) => (onClick ? 'translateY(-2px)' : 'none')};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const Coin = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Side = styled.span<{ side: 'long' | 'short' }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
  background: ${({ side, theme }) =>
    side === 'long' ? theme.colors.success.light : theme.colors.error.light};
  color: ${({ side, theme }) =>
    side === 'long' ? theme.colors.success.dark : theme.colors.error.dark};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 4px;
`;

const Value = styled.span<{ isPositive?: boolean; isNegative?: boolean }>`
  font-size: 1rem;
  font-weight: 500;
  color: ${({ isPositive, isNegative, theme }) =>
    isPositive
      ? theme.colors.success.main
      : isNegative
      ? theme.colors.error.main
      : theme.colors.text.primary};
`;

const PositionCard: React.FC<PositionCardProps> = ({ position, onClick }) => {
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatPercentage = (num: number) => {
    return `${num >= 0 ? '+' : ''}${formatNumber(num)}%`;
  };

  return (
    <Card onClick={onClick}>
      <Header>
        <Coin>{position.coin}</Coin>
        <Side side={position.side}>{position.side.toUpperCase()}</Side>
      </Header>
      <Grid>
        <Metric>
          <Label>Size</Label>
          <Value>{formatNumber(position.size)}</Value>
        </Metric>
        <Metric>
          <Label>Entry Price</Label>
          <Value>${formatNumber(position.entry_price)}</Value>
        </Metric>
        <Metric>
          <Label>Current Price</Label>
          <Value>${formatNumber(position.current_price)}</Value>
        </Metric>
        <Metric>
          <Label>Unrealized P&L</Label>
          <Value isPositive={position.unrealized_pnl > 0} isNegative={position.unrealized_pnl < 0}>
            ${formatNumber(position.unrealized_pnl)}
          </Value>
        </Metric>
        <Metric>
          <Label>Leverage</Label>
          <Value>{position.leverage}x</Value>
        </Metric>
        <Metric>
          <Label>Margin</Label>
          <Value>${formatNumber(position.margin)}</Value>
        </Metric>
      </Grid>
    </Card>
  );
};

export default PositionCard; 